import { Box, Typography, List, ListItem, ListItemText } from '@mui/material'

interface Lounge {
    name: string
    access: string
}

interface Terminal {
    name: string
    domestic?: boolean
    international?: boolean
}

interface TransportToCity {
    taxiEstimateSAR?: string
    metro?: boolean
    bus?: boolean
    notes?: string
}

interface AirportDetail {
    code: string
    name: string
    terminals: number | Terminal[] | null
    lounges: Lounge[]
    transportToCity: string | TransportToCity | null
}

interface AirportInfoProps {
    airports: AirportDetail[]
}

const AirportColumn: React.FC<{ airport: AirportDetail; label: string }> = ({ airport, label }) => {
    const facilities: string[] = []
    if (airport.terminals) {
        if (typeof airport.terminals === 'number') {
            facilities.push(`${airport.terminals} terminals`)
        } else {
            airport.terminals.forEach(t => {
                if (t.name) facilities.push(t.name)
            })
        }
    }
    airport.lounges.forEach(l => {
        if (l.name) facilities.push(l.name)
    })
    if (airport.transportToCity) {
        if (typeof airport.transportToCity === 'string') {
            facilities.push(airport.transportToCity)
        } else {
            const t = airport.transportToCity
            if (t.taxiEstimateSAR) facilities.push(`Taxi: ${t.taxiEstimateSAR}`)
            if (t.metro) facilities.push('Metro available')
            if (t.bus) facilities.push('Bus available')
            if (t.notes) facilities.push(t.notes)
        }
    }

    return (
        <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {airport.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                (IATA: {airport.code})
            </Typography>
            {facilities.length > 0 && (
                <List dense disablePadding sx={{ listStyle: 'disc', pl: 2 }}>
                    {facilities.map((item, i) => (
                        <ListItem key={i} disableGutters disablePadding sx={{ display: 'list-item', py: 0.25 }}>
                            <ListItemText
                                primary={item}
                                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                            />
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    )
}

const AirportInfo: React.FC<AirportInfoProps> = ({ airports }) => {
    const origin = airports[0]
    const destination = airports[1]

    return (
        <Box sx={{ mb: 3 }}>
            <Box
                sx={{
                    border: 1,
                    borderColor: 'grey.200',
                    borderRadius: 2,
                    overflow: 'hidden'
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        '& > :first-of-type': { borderRight: { md: 1 }, borderColor: { md: 'grey.200' }, borderBottom: { xs: 1, md: 0 }, borderBottomColor: { xs: 'grey.200' } }
                    }}
                >
                    {origin && (
                        <Box sx={{ p: 2.5 }}>
                            <AirportColumn airport={origin} label="Origin Airport Facilities" />
                        </Box>
                    )}
                    {destination && (
                        <Box sx={{ p: 2.5 }}>
                            <AirportColumn airport={destination} label="Destination Airport Facilities" />
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

export default AirportInfo
export type { AirportInfoProps, AirportDetail }
